import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown } from 'lucide-react';

/**
 * Searchable single-select dropdown.
 * options: [{ value, label }]. Filters by label. icon: optional lucide element.
 */
export default function SearchableSelect({ options, value, onChange, placeholder, disabled, icon, emptyLabel = 'No matches', onQueryChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // Report the live search query to the parent; reset it whenever the menu closes.
  useEffect(() => {
    if (!open) { setQuery(''); if (onQueryChange) onQueryChange(''); }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = options.find(o => String(o.value) === String(value));
  const filtered = options.filter(o => String(o.label ?? '').toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="relative" ref={ref}>
      {icon && <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-10">{icon}</div>}
      <button type="button" disabled={disabled}
        onClick={() => { setOpen(o => !o); setQuery(''); }}
        className={`w-full text-left ${icon ? 'pl-10' : 'pl-3'} pr-9 py-2 border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-50 disabled:text-gray-400`}>
        <span className={selected ? 'text-gray-800' : 'text-gray-400'}>{selected ? selected.label : placeholder}</span>
        <ChevronDown className={`h-5 w-5 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-hidden flex flex-col">
          <div className="p-2 border-b border-gray-100 sticky top-0 bg-white">
            <div className="relative">
              <Search className="h-4 w-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input autoFocus value={query} onChange={e => { setQuery(e.target.value); if (onQueryChange) onQueryChange(e.target.value); }} placeholder="Search…"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
          </div>
          <div className="overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-400">{emptyLabel}</div>
            ) : filtered.map(o => (
              <button key={o.value} type="button"
                onClick={() => { onChange(String(o.value)); setOpen(false); }}
                className={`block w-full text-left px-3 py-2 text-sm hover:bg-primary-50 ${String(o.value) === String(value) ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700'}`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
